import FilePreviewComponent from "@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue";
import GlobalTable from "@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue";
import {
  ASSIGN_TEMPLATE_ENTITY_TYPE_URL,
  CHECK_STATUS_TEMPLATE,
  DELETE_TEMPLATE_URL,
  get_headers,
  get_project_id,
  get_token,
  LIST_ENTITY_TYPE_ASSIGNMENTS_URL,
  LIST_STATUS_TEMPLATE,
  LIST_TEMPLATE_VIEW_URL,
  UNASSIGN_TEMPLATE_ENTITY_TYPE_URL,
  UPDATE_TEMPLATE
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import { validate_file, validate_non_empty_string } from "@/utils/validations/validate_inputs";
import axios from "axios";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";
import $ from "jquery";
import NavigationBackButton from "@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue";

function get_first_non_empty_value(values = [], fallback = "") {
  return values.find((value) => String(value || "").trim()) || fallback;
}

function build_template_dates(template) {
  const created_at = get_first_non_empty_value([template.created_at, template.date_created], null);
  return {
    created_at,
    updated_at: get_first_non_empty_value([template.updated_at, template.date_modified], created_at),
  };
}

function build_template_identity(template) {
  return {
    name: get_first_non_empty_value([template.name, template.template_name], "Unnamed Template"),
    type: get_first_non_empty_value([template.type, template.template_type], "template"),
  };
}

function get_first_active_template_entity_type_id(template) {
  const assignments = Array.isArray(template.template_entity_types) ? template.template_entity_types : [];
  const active_assignment = assignments.find((assignment) => assignment.is_assigned !== false) || assignments[0];
  return active_assignment?.id || null;
}

export default {
  name: "ViewTemplate",
  mixins: [alert_mixin, formatting_mixins],
  components: { GlobalTable, FilePreviewComponent,NavigationBackButton },
  data: () => ({
    entity_types: [],
    selected_template: {},
    project_id: get_project_id(),
    token: get_token(),
    headers: get_headers(),
    templates: [],
    loading: false,
    selected_file: "",
    unsanitized_template: [],
    table_safe_entity_types: [],
    table_safe_templates: [],
    template_assignments: [],
    entity: null,
    select_template_id: null,
    preview_modal: false,
    templates_table_key: 0,
  }),

  computed: {
    assigned_templates_count() {
      const unique_template_ids = new Set(
        this.template_assignments.map((assignment) => assignment.template_id)
      );
      return unique_template_ids.size;
    },

    recently_updated_count() {
      const now = new Date();
      return this.templates.filter(tpl => {
        const created_at = new Date(tpl.created_at);
        const diff_hours = (now - created_at) / (1000 * 60 * 60);
        return diff_hours <= 24;
      }).length;
    },
  },

  mounted() {
    this.get_uploaded_template();
    this.get_template_assignments();
  },

  methods: {
    normalize_template_record(template) {
      return {
        ...template,
        ...build_template_identity(template),
        ...build_template_dates(template),
        isLoading: false,
      };
    },

    redirect_to_upload_template() {
      this.$router.push("/upload_template");
    },

    assign_template() {
      this.show_success("Template has been assigned successfully.");
    },

    delete_template_file(template_id) {
      this.show_confirm(
        "Are you sure you want to delete?",
        "You won't be able to revert this!",
        () => {
          axios
            .post(
              DELETE_TEMPLATE_URL,
              { template_id: template_id },
              { headers: this.headers }
            )
            .then((response) => {
              if (response.data.status === "success") {
                this.show_success(
                  response.data.message ||
                  "Template has been deleted successfully."
                );
                this.get_uploaded_template();
              } else {
                this.show_error(
                  response.data.message || "An unexpected error occurred."
                );
              }
            })
            .catch((error) => {
              let error_msg = "Failed to delete template";

              if (error.response) {
                try {
                  const response_data =
                    typeof error.response.data === "string"
                      ? JSON.parse(error.response.data)
                      : error.response.data;
                  error_msg = response_data.message || error_msg;
                } catch (e) {
                  error_msg = error.response.data || error_msg;
                }
              }

              this.show_error(error_msg);
            });
        }
      );
    },

    async select_template(template) {
      this.selected_template = { ...template };
      const unsanitized = this.unsanitized_template.find(
        (t) => t.id === template.id
      );
      if (unsanitized) {
        this.selected_template.document_url = unsanitized.document_url;
        this.selected_template.presigned_url = unsanitized.presigned_url;
        this.selected_template.template_name = unsanitized.template_name || unsanitized.name || template["Template Name"];
        this.selected_template.name = unsanitized.name || unsanitized.template_name || template["Template Name"];
      }
      this.preview_modal = true;
    },

    async get_uploaded_template() {
      const params = { project_id: this.project_id };

      this.loading = true;

      try {
        const response = await axios.get(LIST_TEMPLATE_VIEW_URL, {
          headers: this.headers,
          params: params,
        });


        if (response.data.status === "success") {
          const raw_templates = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data.results)
              ? response.data.results
              : [];

          this.templates = raw_templates.map((template) => this.normalize_template_record(template));
          this.unsanitized_template = [...this.templates];
          this.prep_tables();
        }
      } catch (error) {
        this.show_error(
          `Error fetching templates: ${error.response?.data?.message || error.message
          }`
        );
      } finally {
        this.loading = false;
      }
    },

    async fetch_entity_types(template_id) {

      this.select_template_id = template_id

      try {
        const response = await axios.get(LIST_ENTITY_TYPE_ASSIGNMENTS_URL, {
          params: {
            project_id: this.project_id,
            template_id: template_id
          },
          headers: this.headers,
        });

        if (response.data.status === "success") {
          this.entity_types = response.data.data.map((entity_type) => ({
            id: entity_type.id,
            entity_type_name: entity_type.entity_type_name || "N/A",
            description: entity_type.entity_type_description || "N/A",
            is_assigned: Boolean(entity_type.is_assigned),
            isLoading: false,
          }));

          this.table_safe_entity_types = this.entity_types;

        } else {
          this.show_error(
            response.data.message || "Failed to fetch entity types"
          );
        }
      } catch (error) {
        this.show_error(
          "Error fetching entity types: " +
          (error.response?.data?.message || error.message)
        );
      }
    },

    async assign_templates(row) {

      const payload = {
        template_id: this.select_template_id,
        entity_type_id: row.id,
        is_assigned: true,
      };
      row.isLoading = true;

      try {
        const response = await axios.post(ASSIGN_TEMPLATE_ENTITY_TYPE_URL, payload, {
          headers: this.headers,
        });

        const response_data =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;
        if (response_data.status === "success") {
          this.show_success(
            "Template assigned successfully to " + row.entity_type_name
          );
        } else {
          this.show_error(response_data.message || "Assignment failed");
        }
      } catch (error) {
        const error_msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message;

        this.show_error(`Template assignment failed: ${error_msg}`);
      } finally {
        row.isLoading = false;
      }
    },

    async unassign_templates(row) {
       const payload = {
        template_id: this.select_template_id,
        entity_type_id: row.id,
        is_assigned: false,
      };

      try {
        const response = await axios.post(UNASSIGN_TEMPLATE_ENTITY_TYPE_URL, payload, {
          headers: this.headers,
        });

        const response_data = response.data?.status
          ? response.data
          : JSON.parse(response.data);

        if (response_data.status === "success") {
          this.show_success("Template unassigned successfully");
        } else {
          this.show_error(response_data.message || "Unassignment failed");
        }
      } catch (error) {
        const error_msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message;
        this.show_error(`Template unassignment failed: ${error_msg}`);
      }
    },

    async check_template_assignment_status(template_id, entity_type_id) {
      if (!template_id || !entity_type_id) {
        return false;
      }

      const response = await axios.get(CHECK_STATUS_TEMPLATE, {
        params: {
          template_id: template_id,
          entity_type_id: entity_type_id,
        },
        headers: this.headers,
      });

      if (response.data?.status === "success") {
        return response.data.data?.is_assigned || false;
      }
      this.show_error(response.data?.message || "Invalid response from server");
      return false;
    },

    is_template_assigned(entity_type_id) {
      if (!this.selected_template?.id) return false;

      return this.template_assignments.some(
        (assignment) =>
          assignment.template_id === this.selected_template.id &&
          assignment.entity_type_id === entity_type_id
      );
    },

    async toggle_assignment(row) {
      if (row.is_assigned === true) {
        this.assign_templates(row);
      } else {
        this.unassign_templates(row);
      }
    },

    async get_template_assignments() {

      await axios.get(LIST_STATUS_TEMPLATE, {
        headers: this.headers,
      })
        .then(response => {
          const response_data =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;

          if (response_data.status === "success") {
            this.template_assignments =
              response_data.data?.active_assignments || [];
          } else {
            this.show_error(
              response_data.message || "Failed to get template assignments"
            );
          }
        })
        .catch(error => {
          this.show_error(`Error getting template assignments: ${error.message}`);
        })
    },

    configure_template(template_entity_type_id) {
      const template_entity = this.template_entities.find(
        (template_entity) => template_entity.id === template_entity_type_id
      );
      if (!template_entity) return;

      this.$router.push({
        path: "/configure_template",
        query: {
          template_entity_type_id: template_entity_type_id, // snake_case
        },
      });
    },


    prep_tables() {
      if (this.templates.length > 0) {
        this.table_safe_templates = this.templates.map((template) => ({
          __row_id: template.id,
          id: template.id,
          template_name: template.name || template.template_name || "Unnamed Template",
          template_type: String(template.type || template.template_type || "template").toLowerCase(),
          created_at: template.created_at || template.date_created || null,
          updated_at: template.updated_at || template.date_modified || template.created_at || template.date_created || null,
          document_url: template.document_url || null,
          presigned_url: template.presigned_url || null,
          template_entity_type_id: get_first_active_template_entity_type_id(template),
          ID: template.id,
          "Template Name": template.name || template.template_name || "Unnamed Template",
          "Template Type": String(template.type || template.template_type || "template")
            .charAt(0)
            .toUpperCase() + String(template.type || template.template_type || "template").slice(1),
          "Date created": this.format_date(template.created_at || template.date_created),
          "Date modified": this.format_date(template.updated_at || template.date_modified || template.created_at || template.date_created),
        }));
      } else {
        this.table_safe_templates = [];
      }

      this.templates_table_key += 1;
    }
    ,

    populate_update_modal(template) {
      this.selected_template = {
        template_name: template.template_name,
      };
    },

    validate_template_name_input() {
      if (!this.selected_template.template_name) {
        this.show_error("Template name is missing.");
        return false;
      }

      if (!validate_non_empty_string(this.selected_template.template_name)) {
        this.show_error("Template name must not be empty");
        return false;
      }

      return true;
    },

    async handle_file_selection(evt) {
      const file = evt.target.files[0];

      // Clear selection if no file
      if (!file) {
        this.selected_file = null;
        return;
      }

      try {
        // Call the imported validation function
        const validation = await validate_file(file);

        if (!validation.valid) {
          this.show_error(validation.error);
          evt.target.value = ""; // Clear the file input
          this.selected_file = null;
          return;
        }

        // If validation passed, set the selected file
        this.selected_file = file;
      } catch (error) {
        this.show_error("Error validating file");
        evt.target.value = "";
        this.selected_file = null;
      }
    },

    async update_template() {
      this.loading = true;

      const form_data = new FormData();
      form_data.append("template_id", this.selected_template.id);

      // Only append fields that are being changed
      let hasChanges = false;

      if (this.selected_file) {
        form_data.append("new_document_file", this.selected_file);
        hasChanges = true;
      }

      if (
        this.selected_template.template_name !== this.selected_template.name
      ) {
        if (!this.validate_template_name_input()) {
          this.loading = false;
          return;
        }
        form_data.append("new_name", this.selected_template.template_name);
        hasChanges = true;
      }

      if (this.project_id !== this.selected_template.project_id) {
        form_data.append("new_project_id", this.project_id);
        hasChanges = true;
      }

      if (!hasChanges) {
        this.show_error("No changes detected to update");
        this.loading = false;
        return;
      }

      try {
        const config = {
          headers: {
            ...this.headers,
            "Content-Type": "multipart/form-data",
          },
        };

        const response = await axios.post(UPDATE_TEMPLATE, form_data, config);

        if (response.data.status === "success") {
          this.show_success("Template updated successfully.");

          // Update local state
          if (response.data.data) {
            this.selected_template.version = response.data.data.version;
            if (response.data.data.document_url) {
              this.selected_template.document_url =
                response.data.data.document_url;
            }
            if (response.data.data.name) {
              this.selected_template.name = response.data.data.name;
              this.selected_template.template_name = response.data.data.name;
            }
            if (response.data.data.project_id) {
              this.selected_template.project_id = response.data.data.project_id;
            }
          }

          await this.get_uploaded_template();
        } else {
          this.show_error(response.data.message || "Failed to update template");
        }
      } catch (error) {
        let error_msg = "Update failed";
        if (error.response) {
          error_msg = error.response.data.message || error_msg;
          if (error.response.data.data) {
            error_msg +=
              ": " + Object.values(error.response.data.data).join(", ");
          }
        } else {
          error_msg = error.message || error_msg;
        }
        this.show_error(error_msg);
      } finally {
        this.loading = false;
      }
    },

    initialize_data_table() {
      this.$nextTick(() => {
        const table_id = "#trust_types_table";

        if (document.querySelector(table_id)) {
          if ($.fn.DataTable.isDataTable(table_id)) {
            $(table_id).DataTable().destroy(); // Destroy existing
          }
          $(table_id).DataTable({
            responsive: true,
            scrollX: true,
          }); // Reinitialize
        }
      });
    },

    close_the_file_preview(){
      this.preview_modal = false;
    }
  },
};
